<template>
  <div class="invoices-page">
    <!-- No franchise selected: distinct hint (NOT the generic empty table). -->
    <div
      v-if="!hasFranchiseSelected"
      class="invoices-no-franchise-hint"
      data-testid="invoices-no-franchise-hint">
      <i class="pi pi-info-circle" />
      <p>{{ t('Selecione uma franquia no filtro para visualizar as faturas.') }}</p>
    </div>

    <!-- Faturas table. Status filtering + refresh are sourced exclusively from the
         DataTable (filterConfig 'select' column + :refreshButton). No page-level
         filter widgets here. -->
    <invoices-table
      v-else
      :rows="rows"
      :loading="isLoading"
      @row-click="handleRowClick"
      @refresh="loadData" />

    <!-- InvoicesModal is centralized in ModalManager.vue, opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import InvoicesTable from './InvoicesTable.vue';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';
import { usePaymentInvoices, type InvoiceListRow } from '../../../composables/usePaymentInvoices';
import { useFiltro } from '../../../composables/useFiltro';

const { t } = useI18n();
const { listInvoices, error } = usePaymentInvoices();
const { selectedFranchises, filterData } = useFiltro();

const rows = ref<InvoiceListRow[]>([]);
const isLoading = ref<boolean>(false);

// The lista endpoint scopes by a SINGLE franchise_id, so the page loads only the
// first selected franchise (the current backend contract — InvoicesListByFranchiseTest).
// This single-franchise limitation is intentional; do not silently expand to multi-franchise.
const hasFranchiseSelected = computed<boolean>(() => {
  const franchises = selectedFranchises.value as Array<{ id: number | string }>;
  return Boolean(franchises?.[0]?.id);
});

const loadData = async (): Promise<void> => {
  log('InvoicesPage', 'loadData', 'Fetching invoices');
  const franchises = selectedFranchises.value as Array<{ id: number | string; name?: string }>;
  const franchise = franchises?.[0];
  const franchiseId = franchise?.id;
  if (!franchiseId) {
    rows.value = [];
    return;
  }
  isLoading.value = true;
  try {
    const { invoices } = await listInvoices({
      franchise_id: Number(franchiseId),
      date_from: filterData.value?.DataInicio || undefined,
      date_to: filterData.value?.DataFim || undefined,
    });
    // listInvoices swallows fetch failures and returns []; surface the error as a
    // toast instead of a silent empty table.
    if (error.value) {
      eventBus.emit('globalMessage.error', t('Erro ao carregar faturas: ') + error.value);
    }
    // franchise_name (and customer_email) are enriched backend-side in the lista
    // row contract — no frontend injection needed.
    rows.value = invoices;
  } finally {
    isLoading.value = false;
  }
};

const handleRowClick = (id: number): void => {
  log('InvoicesPage', 'handleRowClick', 'Opening detail modal', id);
  eventBus.emit('modal.invoices.open', { id });
};

const handleRefresh = (): void => {
  log('InvoicesPage', 'event:invoices.refresh', 'Received');
  loadData();
};

const handleFilterUpdated = (): void => {
  log('InvoicesPage', 'event:filter.updated', 'Received');
  loadData();
};

onMounted(() => {
  log('InvoicesPage', 'mounted', 'Mounted');
  loadData();
  eventBus.on('invoices.refresh', handleRefresh);
  eventBus.on('filter.updated', handleFilterUpdated);
});

onBeforeUnmount(() => {
  eventBus.off('invoices.refresh', handleRefresh);
  eventBus.off('filter.updated', handleFilterUpdated);
});

defineExpose({ loadData });
</script>

<style scoped>
.invoices-page {
  width: 100%;
}
.invoices-no-franchise-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem;
  text-align: center;
  color: var(--text-color-secondary, #6b7280);
}
.invoices-no-franchise-hint i {
  font-size: 1.75rem;
}
</style>
